import React, { useState, useEffect } from 'react';
import HoneypotField from './HoneypotField';
import './FormularioDemografico.css';
import ciudadesParaguay from '../data/ciudades-paraguay.json';

/**
 * Componente de formulario demográfico opcional
 * Campos: Ciudad, Edad, Sexo
 * @param {Object} props
 * @param {Function} props.onSubmit - Callback con datos demográficos
 * @param {Function} props.onCancel - Callback para cancelar
 */
const FormularioDemografico = ({ onSubmit, onCancel }) => {
  const [datos, setDatos] = useState({
    ciudad: '',
    edad: '',
    sexo: '',
  });
  const [noDeseaCompletar, setNoDeseaCompletar] = useState(false);
  const [errores, setErrores] = useState({});
  const [honeypotValue, setHoneypotValue] = useState(''); // Campo honeypot
  const [ciudades, setCiudades] = useState([]); // Ciudades ordenadas alfabéticamente

  // Cargar y ordenar ciudades alfabéticamente al montar el componente
  useEffect(() => {
    if (ciudadesParaguay && ciudadesParaguay.concept) {
      const ciudadesFormateadas = ciudadesParaguay.concept
        .map((ciudad) => ({ 
          code: String(ciudad.code), 
          display: String(ciudad.display) 
        }))
        .sort((a, b) => a.display.localeCompare(b.display, 'es', { sensitivity: 'base' }));
      setCiudades(ciudadesFormateadas);
    }
  }, []);

  const handleChange = (campo, valor) => {
    setDatos(prev => ({
      ...prev,
      [campo]: valor,
    }));
    
    // Limpiar error del campo
    if (errores[campo]) {
      setErrores(prev => {
        const nuevos = { ...prev };
        delete nuevos[campo];
        return nuevos;
      });
    }
  };

  const validar = () => {
    const nuevosErrores = {};

    if (!noDeseaCompletar) {
      // Si no marcó "no desea completar", validar campos opcionales
      if (datos.edad && (isNaN(datos.edad) || datos.edad < 1 || datos.edad > 120)) {
        nuevosErrores.edad = 'La edad debe ser un número entre 1 y 120';
      }
      
      if (datos.sexo && !['M', 'F'].includes(datos.sexo)) {
        nuevosErrores.sexo = 'Selecciona una opción válida';
      }
    }

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Detectar bot: si el campo honeypot tiene valor, es probablemente un bot
    if (honeypotValue && honeypotValue.trim().length > 0) {
      console.warn('Bot detectado: campo honeypot completado');
      // No enviar datos, pero no mostrar error obvio al bot
      return;
    }
    
    if (!validar()) {
      return;
    }

    if (noDeseaCompletar) {
      onSubmit({
        completo_datos: false,
      });
    } else {
      const datosCompletos = {
        ciudad: datos.ciudad.trim() || null,
        edad: datos.edad ? parseInt(datos.edad) : null,
        sexo: datos.sexo || null,
        completo_datos: !!(datos.ciudad || datos.edad || datos.sexo),
      };
      
      onSubmit(datosCompletos);
    }
  };

  const handleNoDeseaChange = (checked) => {
    setNoDeseaCompletar(checked);
    if (checked) {
      // Limpiar datos si marca "no desea completar"
      setDatos({ ciudad: '', edad: '', sexo: '' });
      setErrores({});
    }
  };

  return (
    <div className="formulario-demografico-overlay">
      <div className="formulario-demografico-modal">
        <div className="formulario-demografico-header">
          <h2>Datos Demográficos (Opcional)</h2>
          <p className="formulario-demografico-subtitle">
            Esta información nos ayuda a mejorar nuestras encuestas
          </p>
        </div>

        <div className="formulario-demografico-disclaimer">
          <div className="disclaimer-icon">ℹ️</div>
          <div className="disclaimer-text">
            <strong>Importante:</strong> Estos datos son utilizados únicamente con fines 
            analíticos y estadísticos. <strong>No se utilizarán para identificar al votante</strong> 
            y se mantendrán completamente anónimos.
          </div>
        </div>

        <form onSubmit={handleSubmit} className="formulario-demografico-form">
          {/* Campo Honeypot - Oculto para detectar bots */}
          <HoneypotField 
            value={honeypotValue} 
            onChange={(e) => setHoneypotValue(e.target.value)} 
          />
          
          <div className="form-group">
            <label htmlFor="ciudad" className="form-label">
              Ciudad
            </label>
            <select
              id="ciudad"
              className="form-select"
              value={datos.ciudad}
              onChange={(e) => handleChange('ciudad', e.target.value)}
              disabled={noDeseaCompletar}
            >
              <option value="">Selecciona una ciudad</option>
              {ciudades.map((ciudad) => (
                <option key={ciudad.code} value={ciudad.code}>
                  {ciudad.display}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="edad" className="form-label">
              Edad
            </label>
            <input
              type="number"
              id="edad"
              className="form-input"
              placeholder="Ej: 25"
              min="1"
              max="120"
              value={datos.edad}
              onChange={(e) => handleChange('edad', e.target.value)}
              disabled={noDeseaCompletar}
            />
            {errores.edad && (
              <span className="form-error">{errores.edad}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="sexo" className="form-label">
              Sexo
            </label>
            <select
              id="sexo"
              className="form-select"
              value={datos.sexo}
              onChange={(e) => handleChange('sexo', e.target.value)}
              disabled={noDeseaCompletar}
            >
              <option value="">Selecciona una opción</option>
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
            </select>
            {errores.sexo && (
              <span className="form-error">{errores.sexo}</span>
            )}
          </div>

          <div className="form-group-checkbox">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={noDeseaCompletar}
                onChange={(e) => handleNoDeseaChange(e.target.checked)}
              />
              <span className="checkbox-text">
                No deseo completar estos datos
              </span>
            </label>
          </div>

          <div className="formulario-demografico-actions">
            {onCancel && (
              <button
                type="button"
                className="btn-cancel"
                onClick={onCancel}
              >
                Cancelar
              </button>
            )}
            <button
              type="submit"
              className="btn-submit"
            >
              Continuar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormularioDemografico;

